import { Sprite, type Texture } from 'pixi.js';
import LAYOUT from './layout.json';

/**
 * Fortune Wheel drawn from its real-3D render (scripts/art/render/wheel.html), seen straight
 * on: the purple back, one white pillow wedge per slice count (the scene tints each copy
 * with its multiplier's colour and turns it into place), the still shadow and gloss over
 * the disc, the gold rim with its bulbs, the gem hub and the red pin. Every layer is placed
 * by its box about the wheel's centre, scaled so the render's disc matches the scene's.
 */

type Box = [number, number, number, number];
const BOXES = LAYOUT.boxes as unknown as Record<string, Box>;
const [CX, CY] = LAYOUT.scene.centre as unknown as [number, number];
const DISC_PX: number = LAYOUT.scene.discPx;
const COUNTS = LAYOUT.scene.counts as unknown as number[];
const BULBS = LAYOUT.scene.bulbs as unknown as [number, number][];
const [, PIN_TIP_Y] = LAYOUT.scene.pinTip as unknown as [number, number];

export const COIN_FRAMES = 8;

export const WHEEL_PIECES = [
  'back',
  ...COUNTS.map((n) => `wedge-${n}`),
  'shade',
  'gloss',
  'rim',
  'hub',
  'pin',
  ...Array.from({ length: COIN_FRAMES }, (_, k) => `coin-${k}`),
  'sparkle',
  'glow',
] as const;
export type WheelPiece = (typeof WHEEL_PIECES)[number];

/** The rendered wedge for a wheel of `count` slices, or null when none was rendered for it. */
export function wedgePiece(count: number): WheelPiece | null {
  return COUNTS.includes(count) ? (`wedge-${count}` as WheelPiece) : null;
}

/** How much the render is scaled to give a disc of `radius` scene pixels. */
export function artScale(radius: number): number {
  return radius / DISC_PX;
}

/** A still layer placed about the wheel's centre, for a disc of `radius`. */
export function placeLayer(tex: Texture, piece: string, radius: number): Sprite {
  const [x, y, w, h] = BOXES[piece]!;
  const k = artScale(radius);
  const s = new Sprite(tex);
  s.position.set((x - CX) * k, (y - CY) * k);
  s.width = w * k;
  s.height = h * k;
  return s;
}

/**
 * Wedge `index` of `count`, tinted `color`, turned into place about the centre. The render
 * points up (centred on -90 degrees), the scene's wedge `index` is centred on
 * (index + 0.5) * slice - 90 degrees, so the sprite turns by (index + 0.5) * slice.
 */
export function placeWedge(tex: Texture, count: number, index: number, color: number, radius: number): Sprite {
  const [x, y, w, h] = BOXES[`wedge-${count}`]!;
  const k = artScale(radius);
  const s = new Sprite(tex);
  s.anchor.set((CX - x) / w, (CY - y) / h);
  s.width = w * k;
  s.height = h * k;
  s.rotation = (index + 0.5) * ((Math.PI * 2) / count);
  s.tint = color;
  return s;
}

/** The rim's bulbs, about the centre, for a disc of `radius`: where their light goes. */
export function bulbPositions(radius: number): { x: number; y: number }[] {
  const k = artScale(radius);
  return BULBS.map(([x, y]) => ({ x: (x - CX) * k, y: (y - CY) * k }));
}

/** Where the pin's point is, about the centre: the pin rocks on it. */
export function pinTipY(radius: number): number {
  return (PIN_TIP_Y - CY) * artScale(radius);
}
